import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { api, ApiError, uploadFile } from '../api';
import { packlistKey, unitLabel, type LineView, type PacklistView } from './types';
import { useUnsavedGuard } from '../ui/unsaved';
import { FileDrop } from '../ui/FileDrop';

const MAX_DESCRIPTION = 2000;

/** "Zgłoś uszkodzenie": description, optional unit (allocated to the line) and a photo from the phone's camera. */
export function DamageForm({ eventId, line, onDone }: { eventId: string; line: LineView; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [description, setDescription] = useState('');
  const [unitId, setUnitId] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [localError, setLocalError] = useState('');
  // The id of the photo once uploaded: a retry after a failed report does not upload it again.
  const uploaded = useRef<{ file: File; id: string } | null>(null);

  const send = useMutation({
    mutationFn: async () => {
      // The photo is uploaded first; the report then attaches it.
      if (photo && uploaded.current?.file !== photo) uploaded.current = { file: photo, id: await uploadFile(photo) };
      const fileIds = photo ? [uploaded.current!.id] : [];
      return api<PacklistView>(`/api/packlists/${eventId}/damage`, {
        method: 'POST',
        body: { lineId: line.id, description: description.trim(), fileIds, ...(unitId ? { unitId } : {}) },
      });
    },
    onSuccess: (view) => {
      queryClient.setQueryData(packlistKey(eventId), view);
      onDone();
    },
  });
  const error = send.error instanceof ApiError ? send.error : send.error ? new ApiError(0, 'unknown', 'Nie udało się wysłać zgłoszenia') : null;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (description.trim() === '') {
      setLocalError('Opisz uszkodzenie.');
      return;
    }
    setLocalError('');
    send.mutate();
  }

  // owner 2026-10-23 21:14: typed and not saved: leaving asks first
  const guardRef = useUnsavedGuard({
    dirty: description.trim() !== '' || unitId !== '' || photo !== null,
    save: async () => {
      if (description.trim() === '') {
        setLocalError('Opisz uszkodzenie.');
        return false;
      }
      await send.mutateAsync();
      return true;
    },
  });
  return (
    <form ref={guardRef} className="form edit" aria-label={`Uszkodzenie: ${line.itemName}`} data-feature-id="packlists.damage" onSubmit={submit}>
      <label className="field">
        <span>Opis uszkodzenia</span>
        <textarea value={description} maxLength={MAX_DESCRIPTION} rows={3} onChange={(e) => setDescription(e.target.value)} />
      </label>
      {line.units.length > 0 && (
        <label className="field">
          <span>Egzemplarz</span>
          <select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
            <option value="">— bez wskazania egzemplarza —</option>
            {line.units.map((u) => (
              <option key={u.id} value={u.id}>
                {unitLabel(u)}
              </option>
            ))}
          </select>
        </label>
      )}
      <FileDrop accept="image/*" disabled={send.isPending} onFiles={(files) => setPhoto(files[0] ?? null)}>
        <label className="field">
          <span>Zdjęcie</span>
          <input type="file" accept="image/*" capture="environment" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
        </label>
      </FileDrop>
      {unitId && <p className="hint">Egzemplarz dostanie w katalogu stan „uszkodzony”.</p>}
      {(localError || error) && (
        <p className="alert" role="alert">
          {localError || error?.message}
        </p>
      )}
      <div className="actions">
        <button className="btn primary" type="submit" disabled={send.isPending}>
          {send.isPending ? 'Wysyłanie…' : 'Wyślij zgłoszenie'}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
